import {
  ProjectReportAnalysisResponseDto,
  ProjectSite,
} from '@/store/modules/projects/types/projects.types';
import { GenerateSingleSiteExcel } from '@/components/reports/export/excel/project/SingleSiteExcel';
import { GenerateMultiSiteExcel } from '@/components/reports/export/excel/project/MultiSiteExcel';
import { GenerateMultiSiteComparisonExcel } from '@/components/reports/export/excel/project/MultiSiteComparisonExcel';
import { GenerateSingleSiteComparisonExcel } from '@/components/reports/export/excel/project/SingleSiteComparisonExcel';
import dayjs from 'dayjs';
import { Workbook } from 'exceljs';
import { ToastProgrammatic as Toast } from 'buefy';

export class ProjectReportExportToExcel {
  public async create(
    reportAnalysisResponse: ProjectReportAnalysisResponseDto[],
    projectTitle: string,
  ) {
    const exportDateTime = dayjs().format('D MMM YYYY');
    const exportTitle = projectTitle + '_' + exportDateTime;
    const csvListArray: any[] = [];
    await Promise.all(
      reportAnalysisResponse.map(
        async (analysis: ProjectReportAnalysisResponseDto, index: number) => {
          csvListArray[index] = await this.handleReportResponse(analysis);
        },
      ),
    );

    const singleAnalysisWorkbook = new Workbook();
    reportAnalysisResponse.forEach(
      (analysis: ProjectReportAnalysisResponseDto, analysisIndex: number) => {
        const singleAnalysisWorksheet = singleAnalysisWorkbook.addWorksheet(
          analysisIndex + 1 + '-' + analysis.analysisTitle,
        );
        csvListArray[analysisIndex].forEach(
          (listItem: any, listItemIndex: number) => {
            singleAnalysisWorksheet.addRow(listItem.data);
            this.applyStyles(singleAnalysisWorksheet, listItem.cellInfo);
          },
        );
        const totalColumns = singleAnalysisWorksheet.columnCount;
        for (let temp = 1; temp <= totalColumns; temp++) {
          switch (temp) {
            case 1:
              singleAnalysisWorksheet.getColumn(temp).width = 100;
              break;
            default:
              singleAnalysisWorksheet.getColumn(temp).width = 50;
              break;
          }
        }
      },
    );
    try {
      singleAnalysisWorkbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, exportTitle + '.xlsx');
      });
    } catch (err) {
      Toast.open({
        message: `Something went wrong. Try again.`,
        position: 'is-top',
        type: 'is-danger',
        duration: 3500,
      });
    } finally {
      Toast.open({
        message: `Export complete.`,
        position: 'is-top',
        type: 'is-dark',
        duration: 3500,
      });
    }
  }

  public applyStyles(singleAnalysisWorksheet: any, cellInfo: any) {
    cellInfo.forEach((item: any, itemIndex: number) => {
      if (item.isEmpty) {
        singleAnalysisWorksheet.getCell(item.name).font = {
          size: 18,
        };
        return;
      }
      singleAnalysisWorksheet.getCell(item.name).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: item.bgColor },
      };
      singleAnalysisWorksheet.getCell(item.name).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      singleAnalysisWorksheet.getCell(item.name).alignment = {
        vertical: 'bottom',
        horizontal: item.alignment,
        wrapText: item.wrapText,
      };
      singleAnalysisWorksheet.getCell(item.name).font = {
        bold: item.bold,
        size: 18,
      };
      if (item.mergeInfo) {
        singleAnalysisWorksheet.mergeCells(item.mergeInfo);
      }
    });
  }

  public async handleReportResponse(
    analysisResponse: ProjectReportAnalysisResponseDto,
  ) {
    const {
      result,
      payload,
      analysisTitle,
      projectTitle,
      projectSites,
    } = analysisResponse;
    switch (payload.comparisonPeriods.length) {
      case 1:
        return await this.generateSinglePeriodExcel(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
      default:
        return await this.generateMultiPeriodExcel(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
    }
  }

  public async generateSinglePeriodExcel(
    result: any,
    payload: any,
    analysisTitle: string,
    projectTitle: string,
    projectSites: ProjectSite[],
  ) {
    switch (projectSites.length) {
      case 1:
        const generateSingleSiteExcel = new GenerateSingleSiteExcel();
        return generateSingleSiteExcel.create(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
      default:
        const generateMultiSiteExcel = new GenerateMultiSiteExcel();
        return generateMultiSiteExcel.create(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
    }
  }

  public async generateMultiPeriodExcel(
    result: any,
    payload: any,
    analysisTitle: string,
    projectTitle: string,
    projectSites: ProjectSite[],
  ) {
    switch (projectSites.length) {
      case 1:
        const generateSingleSiteComparisonExcel = new GenerateSingleSiteComparisonExcel();
        return generateSingleSiteComparisonExcel.create(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
      default:
        const generateMultiSiteComparisonExcel = new GenerateMultiSiteComparisonExcel();
        return generateMultiSiteComparisonExcel.create(
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
        );
    }
  }
}
